import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Imovel, TipoImovel, StatusImovel, TipoAnuncio } from '../../data/Imovel';
import Select from 'react-select'; // Importa o componente de seleção padrão
import { apiRequest } from '../../services/apiService';
import { Comodidade } from '../../data/Comodidades'; // Importa a interface Comodidade

const tipoImovelOptions = Object.values(TipoImovel);
const statusImovelOptions = Object.values(StatusImovel);
const tipoAnuncioOptions = Object.values(TipoAnuncio);

interface ImovelDadosPrincipaisFormProps {
  imovel: Partial<Imovel>;
  handleCaracteristicasChange: (comodidades: Comodidade[]) => void; // Altera para Comodidade[]
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  loading: boolean;
  imovelId?: number | string;
}

const ImovelDadosPrincipaisForm: React.FC<ImovelDadosPrincipaisFormProps> = ({
  imovel,
  handleCaracteristicasChange,
  handleChange,
  handleSubmit,
  loading,
  imovelId,
}) => {
  const [caracteristicasOptions, setCaracteristicasOptions] = useState<{ value: string, label: string }[]>([]);
  const [loadingCaracteristicas, setLoadingCaracteristicas] = useState(false); // Inicia como false, será true no useEffect

  useEffect(() => {
    const fetchCaracteristicas = async () => {
      setLoadingCaracteristicas(true);
      try {
        // Busca todas as comodidades ativas para usar como opções.
        // Usamos um tamanho de página grande para garantir que todos os itens venham.
        const comodidades = await apiRequest<Comodidade[]>('Comodidades/ComodidadesCMB', 'GET', true); // KeyValue é um tipo genérico, melhor especificar
        
        const options = comodidades          
          .map(comodidade => ({
            value: comodidade.id!.toString(),
            label: comodidade.nome,
          }));
        setCaracteristicasOptions(options);
      } catch (error) {
        console.error("Erro ao carregar características:", error);
        // Opcional: Adicionar um estado de erro para notificar o usuário
      } finally {
        setLoadingCaracteristicas(false);
      }
    };

    fetchCaracteristicas();
  }, []); // O array vazio garante que a busca ocorra apenas uma vez

  // Mapeia as comodidades do imóvel para o formato { value: string, label: string }
  // esperado pelo react-select para exibir as opções selecionadas.
  const selectedCaracteristicas = (imovel.comodidades || []).map(comodidade => {
    // O 'value' para o react-select deve ser o ID da comodidade como string.
    // O 'label' para o react-select deve ser o nome da comodidade para exibição.
    // Tentamos encontrar o label na lista de opções carregadas, caso contrário, usamos o nome da comodidade.
    const option = caracteristicasOptions.find(opt => opt.value === comodidade.id!.toString());
    return {
      value: comodidade.id!.toString(), // ID da comodidade como string
      label: option ? option.label : comodidade.nome // Nome da comodidade para exibição
    };
  });

  // Handler para o Select que converte o formato de volta para Comodidade[]
  const handleSelectChange = (newValue: readonly { value: string; label: string }[] | null) => {
    const selectedComodidades: Comodidade[] = newValue ? newValue.map(item => ({
      id: parseInt(item.value, 10), // Converte o ID de string para number
      nome: item.label,
      ativo: true, // Assumimos que as comodidades selecionadas são ativas
    })) : [];
    handleCaracteristicasChange(selectedComodidades);
  };

  
  return (
    <form id="frmDadosImovel" onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-md-8 mb-3">
          <label htmlFor="titulo" className="form-label">Título do Anúncio</label>
          <input type="text" id="titulo" name="titulo" className="form-control" value={imovel.titulo || ''} onChange={handleChange} required />
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="codigoReferencia" className="form-label">Código de Referência</label>
          <input type="text" id="codigoReferencia" name="codigoReferencia" className="form-control" value={imovel.codigoReferencia || ''} onChange={handleChange} />
        </div>
      </div>
      <div className="mb-3">
        <label htmlFor="descricao" className="form-label">Descrição</label>
        <textarea id="descricao" name="descricao" className="form-control" value={imovel.descricao || ''} onChange={handleChange} rows={4} required></textarea>
      </div>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label htmlFor="tipo" className="form-label">Tipo de Imóvel</label>
          <select id="tipo" name="tipo" className="form-select" value={imovel.tipo} onChange={handleChange}>
            {tipoImovelOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="status" className="form-label">Status</label>
          <select id="status" name="status" className="form-select" value={imovel.status} onChange={handleChange}>
            {statusImovelOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="tipoAnuncio" className="form-label">Tipo de Anúncio</label>
          <select id="tipoAnuncio" name="tipoAnuncio" className="form-select" value={imovel.tipoAnuncio} onChange={handleChange}>
            {tipoAnuncioOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </div>

      <h4 className="mt-4">Localização</h4>
      <hr/>
      <div className="row">
        <div className="col-md-3 mb-3">
          <label htmlFor="cep" className="form-label">CEP</label>
          <input type="text" id="cep" name="cep" className="form-control" value={imovel.cep || ''} onChange={handleChange} required />
        </div>
        <div className="col-md-7 mb-3">
          <label htmlFor="logradouro" className="form-label">Logradouro</label>
          <input type="text" id="logradouro" name="logradouro" className="form-control" value={imovel.logradouro || ''} onChange={handleChange} required />
        </div>
        <div className="col-md-2 mb-3">
          <label htmlFor="numero" className="form-label">Número</label>
          <input type="text" id="numero" name="numero" className="form-control" value={imovel.numero || ''} onChange={handleChange} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label htmlFor="complemento" className="form-label">Complemento</label>
          <input type="text" id="complemento" name="complemento" className="form-control" value={imovel.complemento || ''} onChange={handleChange} />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="bairro" className="form-label">Bairro</label>
          <input type="text" id="bairro" name="bairro" className="form-control" value={imovel.bairro || ''} onChange={handleChange} required />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="cidade" className="form-label">Cidade</label>
          <input type="text" id="cidade" name="cidade" className="form-control" value={imovel.cidade || ''} onChange={handleChange} required />
        </div>
        <div className="col-md-2 mb-3">
          <label htmlFor="uf" className="form-label">UF</label>
          <input type="text" id="uf" name="uf" className="form-control" value={imovel.uf || ''} onChange={handleChange} maxLength={2} required />
        </div>
      </div>

      <h4 className="mt-4">Características</h4>
      <hr/>
      <div className="row">
        <div className="col-md-3 mb-3">
          <label htmlFor="areaUtil" className="form-label">Área Útil (m²)</label>
          <input type="number" id="areaUtil" name="areaUtil" className="form-control" value={imovel.areaUtil || 0} onChange={handleChange} required />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="areaTotal" className="form-label">Área Total (m²)</label>
          <input type="number" id="areaTotal" name="areaTotal" className="form-control" value={imovel.areaTotal || 0} onChange={handleChange} required />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="quartos" className="form-label">Quartos</label>
          <input type="number" id="quartos" name="quartos" className="form-control" value={imovel.quartos || 0} onChange={handleChange} required />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="suites" className="form-label">Suítes</label>
          <input type="number" id="suites" name="suites" className="form-control" value={imovel.suites || 0} onChange={handleChange} />
        </div>
      </div>
      <div className="row">
        <div className="col-md-3 mb-3">
          <label htmlFor="banheiros" className="form-label">Banheiros</label>
          <input type="number" id="banheiros" name="banheiros" className="form-control" value={imovel.banheiros || 0} onChange={handleChange} required />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="vagasGaragem" className="form-label">Vagas de Garagem</label>
          <input type="number" id="vagasGaragem" name="vagasGaragem" className="form-control" value={imovel.vagasGaragem || 0} onChange={handleChange} />
        </div>
        <div className="col-md-3 mb-3">
          <label htmlFor="anoConstrucao" className="form-label">Ano de Construção</label>
          <input type="number" id="anoConstrucao" name="anoConstrucao" className="form-control" value={imovel.anoConstrucao || ''} onChange={handleChange} />
        </div>
      </div>
      <div className="mb-3">
        <label htmlFor="caracteristicas" className="form-label">Outras Características</label>
        <Select
          isMulti // Permite a seleção de múltiplas opções
          name="caracteristicas"
          options={caracteristicasOptions} // Opções carregadas da API
          className="basic-multi-select" // Classe para estilização básica do componente
          classNamePrefix="select" // Prefixo para classes internas do react-select
          placeholder="Selecione características" // Ajusta o placeholder para refletir que não é possível criar
          value={selectedCaracteristicas} // As características atualmente selecionadas
          onChange={handleSelectChange} // O handler para quando as opções mudam
          isDisabled={loading || loadingCaracteristicas} // Desabilita enquanto carrega o form ou as opções
          isLoading={loadingCaracteristicas} // Mostra o indicador de loading do react-select
        />
      </div>

      <h4 className="mt-4">Valores</h4>
      <hr/>
      <div className="row">
        <div className="col-md-4 mb-3">
          <label htmlFor="valor" className="form-label">Valor (R$)</label>
          <input type="number" id="valor" name="valor" className="form-control" value={imovel.valor || 0} onChange={handleChange} required />
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="Condominio" className="form-label">Condomínio (R$)</label>
          <input type="number" id="Condominio" name="Condominio" className="form-control" value={imovel.Condominio || ''} onChange={handleChange} />
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="Iptu" className="form-label">IPTU (R$)</label>
          <input type="number" id="Iptu" name="Iptu" className="form-control" value={imovel.Iptu || ''} onChange={handleChange} />
        </div>
      </div>
      
      <h4 className="mt-4">Relacionamentos e Status</h4>
      <hr/>
      <div className="row">
          <div className="col-md-6 mb-3">
              <label htmlFor="proprietarioId" className="form-label">ID do Proprietário</label>
              <input type="number" id="proprietarioId" name="proprietarioId" className="form-control" value={imovel.proprietarioId || ''} onChange={handleChange} required />
          </div>
          <div className="col-md-6 mb-3">
              <label htmlFor="corretorId" className="form-label">ID do Corretor</label>
              <input type="number" id="corretorId" name="corretorId" className="form-control" value={imovel.corretorId || ''} onChange={handleChange} />
          </div>
      </div>

      <div className="mb-3 form-check">
        <input type="checkbox" id="destaque" name="destaque" className="form-check-input" checked={!!imovel.destaque} onChange={handleChange} />
        <label htmlFor="destaque" className="form-check-label">Destaque</label>
      </div>
      <hr />
      <div className="mt-3">
        <button type="submit" className="btn btn-primary" disabled={loading && !imovelId}>
          {loading ? 'Salvando...' : (imovelId ? 'Salvar Alterações' : 'Salvar e Continuar')}
        </button>
        <Link to="/imoveis" className="btn btn-secondary ms-2">Cancelar</Link>
      </div>
    </form>
  );
};

export default ImovelDadosPrincipaisForm;